/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import * as React from "react";

import { UsageLimit } from "@/shared/components/UsageLimit";
import { LimitDictionary } from "@/shared/domain/limits/LimitRecord";
import { formatByteSize } from "@/shared/text";

dayjs.extend(duration);
dayjs.extend(relativeTime);

export interface UsageLimitTripleProps {
  limitSummaryData: LimitDictionary | null;
  currentDataStored: number;
  currentDataDownloaded: number;
  currentComputeCredits: number;
}

export const UsageLimitTriple: React.FC<UsageLimitTripleProps> = ({
  limitSummaryData,
  currentDataStored,
  currentDataDownloaded,
  currentComputeCredits,
}) => {
  let computeCreditLimit;
  let dataDownloadLimit;
  let dataStoredLimit;

  if (limitSummaryData) {
    computeCreditLimit =
      limitSummaryData["actions::free_invocation_compute_credit_millionths"] /
      1_000_000;
    dataDownloadLimit =
      limitSummaryData["files::free_managed_data_download_bytes"];
    dataStoredLimit = limitSummaryData["files::free_managed_data_stored_bytes"];
  }

  const dataStoredLimitLabel = dataStoredLimit
    ? formatByteSize(dataStoredLimit)
    : "N/A";
  const dataDownloadLimitLabel = dataDownloadLimit
    ? formatByteSize(dataDownloadLimit)
    : "N/A";

  const dataStoredPercent =
    dataStoredLimit && dataStoredLimit > 0
      ? (currentDataStored / dataStoredLimit) * 100
      : 0;

  const dataDownloadPercent =
    dataDownloadLimit && dataDownloadLimit > 0
      ? (currentDataDownloaded / dataDownloadLimit) * 100
      : 0;

  const computeCreditPercent =
    computeCreditLimit && computeCreditLimit > 0
      ? (currentComputeCredits / computeCreditLimit) * 100
      : 0;

  return (
    <>
      <UsageLimit
        title="Data Stored"
        currentValue={formatByteSize(currentDataStored)}
        currentPercent={dataStoredPercent}
        limitValue={dataStoredLimitLabel}
        monthly={false}
      />
      <UsageLimit
        title="Data Bandwidth"
        currentValue={formatByteSize(currentDataDownloaded)}
        currentPercent={dataDownloadPercent}
        limitValue={dataDownloadLimitLabel}
        monthly={true}
      />
      <UsageLimit
        title="Processing Units Used (RPU)"
        currentValue={`${+currentComputeCredits.toFixed(2)} RPU`}
        currentPercent={computeCreditPercent}
        limitValue={computeCreditLimit ? `${computeCreditLimit} RPU` : "N/A"}
        monthly={true}
      />
    </>
  );
};
